/**
 * SameDayVisitsCard — ONE card for a date of service with more than one
 * appointment (2026-11-03).
 *
 * **Why this exists.** The page used to render one `EstimatedResponsibility`
 * per same-day appointment, each printing its own deductible. But
 * `ESTIMATE.APPLICABLE_DEDUCTIBLE` is the *policy's* remaining deductible — an
 * annual accumulator written identically onto every row of the policy — so an
 * office visit and an ECHO on one day each carried the full amount and the page
 * collected roughly one extra deductible. On the worked example (office visit
 * allowed $180 / copay $30, ECHO allowed $450 / coinsurance 20%, deductible
 * remaining $150) the page quoted **$390.00 where $270.00 is owed**.
 *
 * appointment-api now allocates the deductible and the out-of-pocket maximum
 * **once per day**, in start-time order, and the BFF passes that result down as
 * `day_accumulators` plus day-adjusted per-line figures. This card renders it:
 *
 *   1. day header — one checkbox for the whole day, the day's total estimate
 *   2. one row per service — allowed and its OWN cost share
 *   3. the day-level figures, shown ONCE
 *   4. "Owed at check-in"
 *
 * **Two rules that are load-bearing, not stylistic:**
 *
 *   - **"Owed at check-in" is `day_owed_minor_units`.** Never add the lines up.
 *     The day total is deductible- and out-of-pocket-adjusted by the backend and
 *     the per-line figures need not sum to it.
 *   - **`oop_remaining_minor_units === null` hides the row.** Null means
 *     *unknown* — the payer returned no out-of-pocket figure, or this is a
 *     self-pay / cash patient. Rendering `$0.00` there tells a cash patient they
 *     have met their maximum and owe nothing, which is the most expensive wrong
 *     answer on this page. `0` with `oop_met` is the genuinely-met case and gets
 *     its own success treatment.
 *
 * **Cost share: both, not one.** `EstimatedResponsibility` shows exactly one
 * cost-share row per card, preferring copay — which is precisely what would hide
 * the ECHO's coinsurance in the headline case. Here each service shows its own,
 * and a service carrying both shows both. The existing varchar handling
 * (`isCoinsuranceZeroOrEmpty`, `formatCoinsurance`) is reused unchanged:
 * coinsurance is an unparsed varchar and neither side parses it.
 */
import { useId } from 'react';
import { Info } from 'lucide-react';

import { Alert } from '@tensaw/design-system/feedback';
import { Card, CardContent } from '@tensaw/design-system/layout';
import { Checkbox, Label } from '@tensaw/design-system/primitives';

import type { DayAccumulators, SameDayAppointment } from '../../api/types';
import { format } from '../../lib/money';
import {
  formatCoinsurance,
  isCoinsuranceZeroOrEmpty,
} from './EstimatedResponsibility';
import {
  hasOutOfPocketNotice,
  outOfPocketFigure,
  OutOfPocketNotices,
} from './OutOfPocketBand';
import {
  allChargeableSelected,
  isLineChargeable,
  isLineEstimable,
  splitFormattedDateTime,
  toggleAllChargeable,
} from './sameDayAppointments';
import { STRIPE_USD_MIN_CENTS } from './stripeMinimum';

const ESTIMATE_TOOLTIP_COPY =
  'Your share of these visits based on your insurance benefits. Includes any ' +
  'deductible, copay, and coinsurance the clinic estimates you’ll owe. ' +
  'Your deductible and out-of-pocket maximum are applied once across the day, ' +
  'not once per visit. Final amounts may change after your insurance ' +
  'processes the claims.';

interface SameDayVisitsCardProps {
  /** Every appointment on the day, in display order (anchor first). */
  lines: readonly SameDayAppointment[];
  /**
   * The day-level figures. `complete === false` means the day could not be
   * priced: the card goes informational and nothing for the day is chargeable.
   */
  day: DayAccumulators;
  selectedIds: ReadonlySet<number>;
  onSelectedIdsChange: (next: Set<number>) => void;
}

export function SameDayVisitsCard({
  lines,
  day,
  selectedIds,
  onSelectedIdsChange,
}: SameDayVisitsCardProps) {
  const checkboxId = useId();
  const tooltipId = useId();

  const collectable = day.complete;
  const chargeable = collectable ? lines.filter(isLineChargeable) : [];
  const anyChargeable = chargeable.length > 0;
  const allSelected = allChargeableSelected(lines, selectedIds);

  // The date, printed ONCE in the header. Taken from the first line's
  // backend-formatted clinic-local string by a pure string split — never
  // through `new Date()`, which would reinterpret it in the browser's
  // timezone (FE ledger #49).
  const dayLabel = splitFormattedDateTime(
    lines[0]?.appointment_date_formatted,
  ).date;
  const serviceNames = lines
    .map((line) => line.appointment_type_name)
    .filter((name): name is string => Boolean(name));
  const title = serviceNames.length > 0 ? serviceNames.join(' + ') : null;

  // The day's total estimate, before anything already paid. Summing the LINE
  // totals is sound — they are the backend's day-adjusted figures, already
  // deductible- and out-of-pocket-allocated. What must never be summed is
  // "owed at check-in"; that comes from `day_owed_minor_units`.
  const dayTotal = lines.reduce(
    (acc, line) => acc + line.estimate_total_minor_units,
    0,
  );
  const dayPaid = lines.reduce(
    (acc, line) => acc + line.estimate_paid_minor_units,
    0,
  );

  return (
    <Card data-testid="estimate-card-day">
      <CardContent className="p-0">
        {/* --- Band 1: the day header -------------------------------------- */}
        <div className="border-b border-border px-4 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-3 sm:gap-4">
            <div className="flex items-start gap-2 sm:gap-3">
              {anyChargeable && (
                <Checkbox
                  id={checkboxId}
                  checked={allSelected}
                  onCheckedChange={(next: boolean | 'indeterminate') => {
                    onSelectedIdsChange(toggleAllChargeable(lines, next === true));
                  }}
                  aria-label={
                    dayLabel
                      ? `Include my ${dayLabel} visits in this payment`
                      : 'Include my visits in this payment'
                  }
                  className="mt-1"
                />
              )}
              <div className="flex items-center gap-1.5">
                <Label
                  htmlFor={anyChargeable ? checkboxId : undefined}
                  className="text-[15px] font-semibold sm:text-base text-foreground"
                >
                  {dayLabel ? `Your visits on ${dayLabel}` : 'Your visits'}
                </Label>
                {/* Native title + aria-describedby: design-system has no
                    Tooltip primitive and one micro-interaction is not worth
                    upstream work. Same pattern as the single-visit card. */}
                <button
                  type="button"
                  aria-label="About estimated responsibility"
                  aria-describedby={tooltipId}
                  title={ESTIMATE_TOOLTIP_COPY}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
                <span id={tooltipId} className="sr-only">
                  {ESTIMATE_TOOLTIP_COPY}
                </span>
              </div>
            </div>
            {collectable && (
              <div className="shrink-0 text-right text-sm text-muted-foreground">
                <div className="text-base font-semibold text-primary tabular-nums">
                  <span className="sr-only">Total estimate </span>
                  {format(dayTotal)}
                </div>
                {dayPaid > 0 && (
                  <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
                    Already paid {format(dayPaid)}
                  </div>
                )}
              </div>
            )}
          </div>
          {title && (
            <p className="mt-1 text-xs text-muted-foreground">{title}</p>
          )}
        </div>

        {/* --- Band 2: one row per service --------------------------------- */}
        <div className="divide-y divide-border border-b border-border">
          {lines.map((line) => (
            <ServiceLineRow key={line.appointment_id} line={line} />
          ))}
        </div>

        {/* --- Band 3: the day-level figures, shown ONCE -------------------- */}
        {collectable && <DayFigures day={day} lines={lines} />}

        {/* --- Band 4: what is owed, or why we cannot say -------------------
            Dropped ENTIRELY on a met maximum: `OwedAtCheckin` renders nothing
            there (the band above already explains it), and a band kept for a
            null child is an empty padded strip under the card. */}
        {!(collectable && day.day_owed_minor_units === 0 && day.oop_met) && (
          <div className="px-4 py-5 sm:px-6">
            {collectable ? (
              <OwedAtCheckin
                dayOwedMinorUnits={day.day_owed_minor_units}
                anyChargeable={anyChargeable}
                oopMet={day.oop_met}
              />
            ) : (
              <DayUnpricedNotice />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One service inside the day card: its name and time, its allowed amount, and
 * its OWN cost share — copay, coinsurance, or both.
 */
function ServiceLineRow({ line }: { line: SameDayAppointment }) {
  const { time } = splitFormattedDateTime(line.appointment_date_formatted);
  const noEstimate = !line.has_estimate;
  const unpriceable = !noEstimate && !isLineEstimable(line);
  const pending = noEstimate || unpriceable;
  const remaining = line.estimate_remaining_minor_units;
  const subMinimum =
    !pending &&
    !line.appointment_cancelled &&
    remaining > 0 &&
    remaining < STRIPE_USD_MIN_CENTS;

  const allowed = line.allowed_minor_units ?? 0;
  const copay = line.copay_minor_units;
  const coinsurance = line.coinsurance;
  const showCoinsurance =
    coinsurance !== null && !isCoinsuranceZeroOrEmpty(coinsurance);
  // BOTH when both: the single-card rule preferred copay, which is exactly what
  // would hide the ECHO's coinsurance here. Neither set -> "Copay $0.00", so
  // the patient sees the visit has no copay rather than a missing row.
  const showCopay = copay > 0 || !showCoinsurance;
  const deductibleAllocated = line.deductible_allocated_minor_units;

  return (
    <div className="px-4 py-4 sm:px-6">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium text-foreground">
          {line.appointment_type_name ?? 'Visit'}
        </p>
        {time && (
          <p className="shrink-0 text-xs text-muted-foreground">{time}</p>
        )}
      </div>
      {line.appointment_cancelled && (
        <p className="mt-1 text-xs text-muted-foreground">Cancelled</p>
      )}
      {!pending && (
        <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
          {allowed > 0 && (
            <Figure label="Estimated allowed" value={format(allowed)} />
          )}
          {showCopay && (
            <Figure label="Copay" value={format(Math.max(copay, 0))} />
          )}
          {showCoinsurance && (
            <Figure
              label="Coinsurance"
              value={formatCoinsurance(coinsurance as string)}
            />
          )}
          {/* Only when this service actually consumed deductible. `0` on every
              line after it is exhausted, which is the over-collection fix made
              visible — omitted rather than printed as "$0.00". */}
          {deductibleAllocated !== undefined && deductibleAllocated > 0 && (
            <Figure
              label="Deductible applied"
              value={format(deductibleAllocated)}
            />
          )}
        </dl>
      )}
      {pending && (
        <p className="mt-1 text-xs text-muted-foreground">
          {unpriceable
            ? 'Estimate not available yet — settle this visit at check-in.'
            : 'No estimate yet — settle this visit at check-in.'}
        </p>
      )}
      {subMinimum && (
        <p className="mt-1 text-xs text-muted-foreground">
          {`${format(remaining)} remaining — under ${format(
            STRIPE_USD_MIN_CENTS,
          )}, please pay at the clinic.`}
        </p>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground tabular-nums">{value}</dd>
    </div>
  );
}

/**
 * The day-level figures, each shown ONCE. This band is the whole point of the
 * merged card: these used to be printed on every appointment's card.
 */
function DayFigures({
  day,
  lines,
}: {
  day: DayAccumulators;
  lines: readonly SameDayAppointment[];
}) {
  const insurancePay = lines.reduce(
    (acc, line) => acc + line.insurance_estimated_pay_minor_units,
    0,
  );
  const allocated = day.deductible_allocated_minor_units;
  const remainingDeductible = day.deductible_remaining_minor_units;

  const rows: Array<{ label: string; value: string }> = [];
  if (allocated !== null && allocated > 0) {
    rows.push({ label: 'Deductible applied today', value: format(allocated) });
  }
  if (remainingDeductible !== null) {
    rows.push({
      label: 'Deductible remaining before today',
      value: format(remainingDeductible),
    });
  }
  // The out-of-pocket row and its notices live in `OutOfPocketBand` -- ONE
  // implementation, shared with the single-visit card. Three states (unknown ->
  // nothing, met -> a success alert, partially capped -> the explanation) that
  // a second copy would eventually collapse, and every way of collapsing them
  // tells a patient something false. See that module.
  const oopRow = outOfPocketFigure(day);
  if (oopRow !== null) rows.push(oopRow);
  if (insurancePay > 0) {
    rows.push({
      label: 'Insurance estimated to pay',
      value: format(insurancePay),
    });
  }

  if (rows.length === 0 && !hasOutOfPocketNotice(day)) return null;

  return (
    <div className="border-b border-border bg-muted/20 px-4 py-4 sm:px-6">
      {rows.length > 0 && (
        <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {rows.map((row) => (
            <Figure key={row.label} label={row.label} value={row.value} />
          ))}
        </dl>
      )}
      <OutOfPocketNotices
        day={day}
        className={rows.length > 0 ? 'mt-3' : undefined}
      />
    </div>
  );
}

/**
 * "Owed at check-in" — `day_owed_minor_units`, verbatim. The per-line figures
 * are not summed here and must not be: the day total is the backend's
 * deductible- and out-of-pocket-adjusted number.
 */
function OwedAtCheckin({
  dayOwedMinorUnits,
  anyChargeable,
  oopMet,
}: {
  dayOwedMinorUnits: number | null;
  anyChargeable: boolean;
  /** The day owes nothing because the plan's maximum is MET, not because it
   *  was paid. The band above already says so, in the patient's terms. */
  oopMet: boolean;
}) {
  if (dayOwedMinorUnits === null) return <DayUnpricedNotice />;
  if (dayOwedMinorUnits === 0) {
    // A met maximum gets ONE explanation, not two. "These visits' estimates
    // are already covered" is the settled-estimate wording: it implies money
    // was collected. On a met maximum nothing was paid -- the plan pays 100%,
    // which the "Max out-of-pocket met" alert directly above has just said --
    // so repeating it here is at best noise and at worst tells the patient
    // they have already paid something they have not.
    if (oopMet) return null;
    return (
      <Alert variant="success" title="Nothing more to pay for these visits">
        These visits&rsquo; estimates are already covered.
      </Alert>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-4">
      <p className="text-sm text-muted-foreground">
        {anyChargeable ? 'Owed at check-in' : 'Owed at check-in (pay at the clinic)'}
      </p>
      <div className="text-base font-semibold text-foreground tabular-nums">
        {format(dayOwedMinorUnits)}
      </div>
    </div>
  );
}

/**
 * The day could not be priced, so nothing for it can be paid online — the
 * charge endpoint refuses the estimate component outright.
 *
 * The tempting fallback is to show the stored per-appointment estimates. Do not
 * add it: they double-count the policy's remaining deductible, which is the
 * entire defect this card exists to fix.
 */
function DayUnpricedNotice() {
  return (
    <Alert
      variant="info"
      title={"We can’t calculate today’s total right now"}
    >
      Please pay at the front desk when you arrive. Any past balance on this page
      can still be paid online.
    </Alert>
  );
}
